/**
 * Demo-mode flag + fixed demo identifiers.
 *
 * When `PUBLIC_DEMO=1` (set only by the `build:demo` script that ships
 * demo.clickweave.co) the dashboard runs against bundled fixtures
 * instead of the live API, with Clerk auth bypassed. This powers the
 * isolated `/embed` marketing surface — the *real* product UI rendered
 * against a seeded, read-only dataset that never touches the audit
 * runner, credits, or pixel ingest. See `./demo-fixtures.ts`.
 *
 * `DEMO` is a build-time constant (Vite inlines `PUBLIC_*`), so every
 * `if (DEMO)` branch is dead-code-eliminated from the normal
 * production bundle — the fixtures never ship to real users, and the
 * `/embed` route `redirect`s home outside a demo build.
 */
export const DEMO: boolean = import.meta.env.PUBLIC_DEMO === '1';

/**
 * Embed mode — a demo build loaded with `?embed=1` (marketing's mid-page
 * demo slices). Renders the routed surface WITHOUT the app chrome
 * (sidebar / mobile drawer / command palette) and locks navigation to the
 * boot pathname: search-param changes (tabs, filters, metric toggles)
 * keep working, but links that would change route are bounced back.
 * Read once at boot — the flag can't flicker as the SPA rewrites the URL.
 */
export const DEMO_EMBED: boolean =
  DEMO && typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('embed');

// Styling hook for embed mode (see globals.css: flush island, no double
// edge inside the marketing iframe's own rounded frame).
if (DEMO_EMBED) document.documentElement.dataset.embed = '1';

/**
 * Any demo surface rendered inside an iframe — www's hero frame and its
 * mid-page embed slices both. A nested browsing context writes to the
 * TOP-LEVEL session history, so these frames navigate by `replace`
 * rather than `push` (see `main.tsx`): otherwise every tab, filter and
 * metric toggle a visitor clicks inside the demo lands another entry on
 * the marketing page's Back button.
 */
export const DEMO_IN_IFRAME: boolean =
  DEMO && typeof window !== 'undefined' && window.self !== window.top;

/**
 * Marketing hero frame — a demo build inside an iframe WITHOUT `?embed=1`
 * (www's HeroSection). It keeps the full chrome, unlike an embed slice, but
 * boots on the rail alone: the section panel would claim 184px of an already
 * small frame, and the hero's poster — which the live app cross-fades over —
 * shows no panel, so opening one shifts the whole app on arrival. Only the
 * boot section is suppressed; clicking the section row brings the panel back.
 */
export const DEMO_FRAMED: boolean = DEMO_IN_IFRAME && !DEMO_EMBED;

/**
 * Standalone demo tab — a demo build viewed as the top-level page
 * (demo.clickweave.co opened directly, e.g. the hero's "View full demo"
 * link). False inside ANY iframe: the marketing hero frames the
 * full-chrome demo without `?embed=1`, and those pages carry their own
 * CTAs — only the standalone tab shows the DemoBar frame.
 */
export const DEMO_STANDALONE: boolean =
  DEMO && !DEMO_EMBED && typeof window !== 'undefined' && window.self === window.top;

/** The single seeded site every demo surface renders. */
export const DEMO_SITE_UUID = 'sit-demo00000000';

/** Fixed org id the demo-aware `useActiveOrg` returns (no Clerk). */
export const DEMO_ORG_ID = 'org_demo00000000';
